import { useEffect, useState } from "react";
import { useClerk } from "@clerk/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Avatar, ImageUploader, PageHeader, Placeholder } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { LocationDialog } from "@/components/LocationDialog";
import { TwoStepSignIn } from "@/components/AccountSecurity";

const LocationSection = () => {
  const { me, refresh } = useMe();
  const [open, setOpen] = useState(false);
  const a = me?.location?.area;
  const where = a ? [a.city, a.region, a.country].filter(Boolean).join(", ") : "";
  return (
    <section className="surface flex flex-wrap items-center gap-3 p-5 sm:p-6" data-testid="settings-location">
      <div className="flex-1"><h2 className="text-xl">Your location</h2><p className="text-xs text-slate-500" data-testid="settings-location-value">{where || "Not set"} · used for local and national news and weather.</p></div>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="settings-location-btn">{where ? "Change" : "Set location"}</Button>
      <LocationDialog open={open} setOpen={setOpen} initial={me?.location} onSaved={refresh} />
    </section>
  );
};

const Row = ({ title, text, children }) => (
  <div className="flex items-center justify-between gap-4 py-3.5"><div><p className="text-sm font-medium">{title}</p>{text && <p className="text-xs text-slate-500">{text}</p>}</div>{children}</div>
);

export default function Settings() {
  const { me, refresh } = useMe();
  const { signOut, openUserProfile } = useClerk();
  const [f, setF] = useState({ name: me.name, handle: me.handle, bio: me.bio, suburb: me.suburb, interests: (me.interests || []).join(", ") });
  const [avatar, setAvatar] = useState(me.avatar ? [me.avatar] : []);
  const [cover, setCover] = useState(me.cover ? [me.cover] : []);
  const [s, setS] = useState({ show_suburb: true, ...me.settings });
  const [blocked, setBlocked] = useState([]);
  useEffect(() => { api.get("/blocks").then((r) => setBlocked(r.data)); }, []);

  const save = async () => {
    try {
      await api.patch("/me", { ...f, interests: f.interests.split(",").map((x) => x.trim().toLowerCase()).filter(Boolean), avatar: avatar[0] || "", cover: cover[0] || "", settings: s });
      await refresh(); toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const unblock = async (id) => { await api.post(`/people/${id}/unblock`); setBlocked(blocked.filter((b) => b.user_id !== id)); };

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="settings-page">
      <PageHeader title="Settings & privacy" />
      <section className="surface space-y-4 p-5 sm:p-6">
        <h2 className="text-xl">Profile</h2>
        <div className="flex items-center gap-4"><Avatar user={{ ...me, avatar: avatar[0] }} size={64} /><ImageUploader value={avatar} onChange={(v) => setAvatar(v.slice(-1))} max={1} testid="avatar-upload" /></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input data-testid="settings-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" />
          <Input data-testid="settings-handle-input" value={f.handle} onChange={(e) => setF({ ...f, handle: e.target.value })} placeholder="Handle" />
          <Input data-testid="settings-suburb-input" value={f.suburb} onChange={(e) => setF({ ...f, suburb: e.target.value })} placeholder="Suburb (used for Local feed)" />
          <Input data-testid="settings-interests-input" value={f.interests} onChange={(e) => setF({ ...f, interests: e.target.value })} placeholder="Interests, comma separated" />
        </div>
        <Textarea data-testid="settings-bio-input" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} placeholder="A little about you" />
        <div><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Cover photo</p><ImageUploader value={cover} onChange={(v) => setCover(v.slice(-1))} max={1} testid="cover-upload" /></div>
      </section>
      <LocationSection />
      <TwoStepSignIn />
      <Link to="/terms" className="block text-sm text-slate-500 underline" data-testid="settings-terms-link">Terms of Use</Link>
      <Link to="/privacy-policy" className="block text-sm text-slate-500 underline" data-testid="settings-privacy-policy-link">Privacy Policy</Link>
      <section className="surface divide-y divide-border p-5 sm:p-6">
        <h2 className="pb-2 text-xl">Privacy</h2>
        <Row title="Who can see my Facts" text="Facts are public on Middle. Delete any post you don't want shown."><span className="text-sm text-slate-500" data-testid="settings-facts-public">Everyone</span></Row>
        <Row title="Who can message me" text="Only people you follow who also follow you."><span className="text-sm text-slate-500" data-testid="settings-message-mutual">Mutual follows</span></Row>
        <Row title="Let members open my follower lists" text="Your follower and following numbers always show. Turn this off to stop other members opening the lists."><Switch data-testid="settings-follow-lists-visible" checked={s.follow_lists_visible !== false} onCheckedChange={(v) => setS({ ...s, follow_lists_visible: v })} /></Row>
        <Row title="Show my suburb on my profile"><Switch data-testid="settings-show-suburb" checked={s.show_suburb} onCheckedChange={(v) => setS({ ...s, show_suburb: v })} /></Row>
      </section>
      <div className="flex justify-end"><Button data-testid="settings-save-btn" className="rounded-full px-7" onClick={save}>Save changes</Button></div>
      <section className="surface p-5 sm:p-6">
        <h2 className="text-xl">Blocked people</h2>
        {blocked.length === 0 ? <p className="mt-2 text-sm text-slate-500">You haven't blocked anyone.</p> : blocked.map((b) => (
          <div key={b.user_id} className="mt-3 flex items-center gap-3"><Avatar user={b} size={32} /><span className="flex-1 text-sm">{b.name}</span><Button size="sm" variant="outline" data-testid={`unblock-${b.user_id}`} onClick={() => unblock(b.user_id)}>Unblock</Button></div>
        ))}
      </section>
      <section className="surface flex flex-wrap gap-2 p-5 sm:p-6">
        <a href="/privacy" data-testid="settings-privacy-centre" className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-slate-400">Privacy Centre & cookie settings</a>
        <a href="/preferences" data-testid="settings-preferences" className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-slate-400">Discovery & recommendations</a>
      </section>
      <section className="surface space-y-3 p-5 sm:p-6">
        <h2 className="text-xl">Account</h2>
        <p className="text-sm text-slate-500">Signed in as {me.email}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" data-testid="settings-manage-account" onClick={() => openUserProfile()}>Email, password & sign-in methods</Button>
          <Button variant="outline" className="rounded-full" data-testid="settings-signout-btn" onClick={() => signOut()}>Sign out</Button>
        </div>
        <Placeholder>Sign in with Apple</Placeholder>
      </section>
    </div>
  );
}
